import {
  COUNTRY_CHECKLIST,
  COUNTRY_STATUS_TITLES,
  COUNTRY_STATUSES,
  type Country,
  type CountryStatus,
  DATA_REGIONS,
  DOC_LANGS,
  LEGAL_KIND_TITLES,
  LEGAL_KINDS,
  type LegalKind,
  SANCTION_STATUSES,
  SANCTION_TITLES,
  type Subprocessor,
  type TaxRate,
} from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import { type FormEvent, type ReactNode, useState } from 'react';
import { ApiError, api } from '../../lib/api';
import { date } from '../../lib/format';
import { Button } from '../../ui/button';
import { Dialog } from '../../ui/dialog';
import { Badge, EmptyState, ErrorBox, Spinner, type Tone, toast } from '../../ui/feedback';
import { Checkbox, Field, Input, Select, Textarea } from '../../ui/form';
import { Panel } from '../../ui/layout';
import {
  countriesQuery,
  currencyRatesQuery,
  legalQuery,
  num,
  partnersQuery,
  planPricesQuery,
  sanctionsQuery,
  subprocessorsQuery,
  taxRatesQuery,
} from './market-queries';
import { plansQuery, todayIso } from './shared';

const STATUS_TONE: Record<CountryStatus, Tone> = { closed: 'neutral', pilot: 'warning', ready: 'success' };
const SANCTION_TONE: Record<string, Tone> = { allowed: 'success', permit: 'warning', blocked: 'danger' };

/** Сохранение с сообщением и обновлением кабинета; ошибки по полям собирает форма */
function useSave<T = unknown>(
  fn: (v: T) => Promise<unknown>,
  done: string,
  onDone?: () => void,
  onFieldErrors?: (e: Record<string, string>) => void,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['platform'] });
      toast(done);
      onDone?.();
    },
    onError: (err) => {
      if (err instanceof ApiError && onFieldErrors) onFieldErrors(err.fieldErrors());
    },
  });
}

function dialogFooter(formId: string, onClose: () => void, pending: boolean, label = 'Сохранить') {
  return (
    <>
      <Button variant="ghost" onClick={onClose}>
        Отмена
      </Button>
      <Button variant="primary" type="submit" form={formId} loading={pending}>
        {label}
      </Button>
    </>
  );
}

function Loading<T>({
  q,
  children,
}: {
  q: { isPending: boolean; isError: boolean; error: unknown; data: T | undefined };
  children: (d: T) => ReactNode;
}) {
  if (q.isPending) return <Spinner />;
  if (q.isError || q.data === undefined) return <ErrorBox error={q.error} />;
  return <>{children(q.data)}</>;
}

/* ───────────────────────── Страны (МР-01, МР-02, МР-11, МР-23) ───────────────────────── */

function CountryDialog({ country, onClose }: { country: Country | 'new'; onClose: () => void }) {
  const isNew = country === 'new';
  const partners = useQuery(partnersQuery);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [f, setF] = useState({
    code: isNew ? '' : country.code,
    name: isNew ? '' : country.name,
    nameEn: isNew ? '' : country.nameEn,
    languages: isNew ? 'ru' : country.languages.join(', '),
    currency: isNew ? '' : country.currency,
    legalPack: isNew ? '' : (country.legalPack ?? ''),
    dataRegion: isNew ? '' : (country.dataRegion ?? ''),
    partnerId: isNew ? '' : (country.partnerId ?? ''),
    status: (isNew ? 'closed' : country.status) as CountryStatus,
    sanctions: (isNew ? 'allowed' : country.sanctions) as Country['sanctions'],
    sanctionsDecision: isNew ? '' : (country.sanctionsDecision ?? ''),
    sellerEntity: isNew ? '' : (country.sellerEntity ?? ''),
    governingLaw: isNew ? '' : (country.governingLaw ?? ''),
    jurisdiction: isNew ? '' : (country.jurisdiction ?? ''),
    targetRevenue: isNew || country.targetRevenue == null ? '' : String(country.targetRevenue),
    notes: isNew ? '' : (country.notes ?? ''),
  });
  const [checklist, setChecklist] = useState<Country['checklist']>(isNew ? {} : country.checklist);
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) =>
    setF((x) => ({ ...x, [k]: e.target.value }));
  const orNull = (s: string) => s.trim() || null;
  const langs = f.languages
    .split(/[,\s]+/)
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean);

  const save = useSave(
    () => {
      if (isNew)
        return api('/platform/countries', {
          body: {
            code: f.code,
            name: f.name,
            nameEn: f.nameEn,
            languages: langs,
            currency: f.currency,
            legalPack: orNull(f.legalPack),
            dataRegion: orNull(f.dataRegion),
          },
        });
      return api(`/platform/countries/${country.code}`, {
        method: 'PATCH',
        body: {
          name: f.name,
          nameEn: f.nameEn,
          languages: langs,
          currency: f.currency,
          legalPack: orNull(f.legalPack),
          dataRegion: orNull(f.dataRegion),
          partnerId: f.partnerId || null,
          status: f.status,
          sanctions: f.sanctions,
          sanctionsDecision: orNull(f.sanctionsDecision),
          sellerEntity: orNull(f.sellerEntity),
          governingLaw: orNull(f.governingLaw),
          jurisdiction: orNull(f.jurisdiction),
          targetRevenue: f.targetRevenue.trim() ? Number(f.targetRevenue) : null,
          notes: orNull(f.notes),
          checklist,
        },
      });
    },
    isNew ? 'Страна добавлена' : 'Страна сохранена',
    onClose,
    setErrors,
  );
  const submit = (e: FormEvent) => {
    e.preventDefault();
    setErrors({});
    save.mutate(undefined);
  };
  const doneCount = COUNTRY_CHECKLIST.filter((c) => checklist[c.code]?.done).length;

  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title={isNew ? 'Новая страна' : `${country.name} (${country.code})`}
      description={
        isNew
          ? 'Новая страна закрыта: статус «готова к продаже» ставится, когда закрыт весь чек-лист.'
          : undefined
      }
      width="lg"
      footer={dialogFooter('country-form', onClose, save.isPending, isNew ? 'Добавить' : 'Сохранить')}
    >
      <form id="country-form" onSubmit={submit} className="grid gap-4 sm:grid-cols-2" noValidate>
        {isNew ? (
          <Field
            label="Код страны"
            htmlFor="cn-code"
            hint="Две латинские буквы, например UZ"
            error={errors.code}
          >
            <Input
              id="cn-code"
              maxLength={2}
              value={f.code}
              onChange={(e) => setF((x) => ({ ...x, code: e.target.value.toUpperCase() }))}
              invalid={!!errors.code}
            />
          </Field>
        ) : null}
        <Field label="Название" htmlFor="cn-name" error={errors.name}>
          <Input id="cn-name" value={f.name} onChange={set('name')} invalid={!!errors.name} />
        </Field>
        <Field label="Название по-английски" htmlFor="cn-nameen" error={errors.nameEn}>
          <Input id="cn-nameen" value={f.nameEn} onChange={set('nameEn')} invalid={!!errors.nameEn} />
        </Field>
        <Field
          label="Языки"
          htmlFor="cn-lang"
          hint={`Коды через запятую: ${DOC_LANGS.join(', ')}`}
          error={errors.languages}
        >
          <Input id="cn-lang" value={f.languages} onChange={set('languages')} invalid={!!errors.languages} />
        </Field>
        <Field label="Валюта" htmlFor="cn-cur" hint="Три буквы, например KZT" error={errors.currency}>
          <Input
            id="cn-cur"
            maxLength={3}
            value={f.currency}
            onChange={(e) => setF((x) => ({ ...x, currency: e.target.value.toUpperCase() }))}
            invalid={!!errors.currency}
          />
        </Field>
        <Field
          label="Пакет форм"
          htmlFor="cn-pack"
          hint="Например KZ — формы актов страны"
          error={errors.legalPack}
        >
          <Input id="cn-pack" value={f.legalPack} onChange={set('legalPack')} />
        </Field>
        <Field label="Регион хранения данных" htmlFor="cn-region" error={errors.dataRegion}>
          <Select id="cn-region" value={f.dataRegion} onChange={set('dataRegion')}>
            <option value="">не задан</option>
            {DATA_REGIONS.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </Select>
        </Field>
        {isNew ? null : (
          <>
            <Field label="Статус" htmlFor="cn-status" error={errors.status}>
              <Select
                id="cn-status"
                value={f.status}
                onChange={(e) => setF((x) => ({ ...x, status: e.target.value as CountryStatus }))}
              >
                {COUNTRY_STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {COUNTRY_STATUS_TITLES[s]}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Партнёр страны" htmlFor="cn-partner" error={errors.partnerId}>
              <Select id="cn-partner" value={f.partnerId} onChange={set('partnerId')}>
                <option value="">без партнёра</option>
                {(partners.data ?? []).map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Санкции" htmlFor="cn-sanc" error={errors.sanctions}>
              <Select
                id="cn-sanc"
                value={f.sanctions}
                onChange={(e) => setF((x) => ({ ...x, sanctions: e.target.value as Country['sanctions'] }))}
              >
                {SANCTION_STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {SANCTION_TITLES[s]}
                  </option>
                ))}
              </Select>
            </Field>
            <Field
              label="Решение юриста"
              htmlFor="cn-sdec"
              hint="Обязательно при «нужно разрешение» и «закрыта»"
              error={errors.sanctionsDecision}
            >
              <Input id="cn-sdec" value={f.sanctionsDecision} onChange={set('sanctionsDecision')} />
            </Field>
            <Field label="Юрлицо-продавец" htmlFor="cn-seller" error={errors.sellerEntity}>
              <Input id="cn-seller" value={f.sellerEntity} onChange={set('sellerEntity')} />
            </Field>
            <Field label="Применимое право" htmlFor="cn-law" error={errors.governingLaw}>
              <Input id="cn-law" value={f.governingLaw} onChange={set('governingLaw')} />
            </Field>
            <Field label="Подсудность" htmlFor="cn-jur" error={errors.jurisdiction}>
              <Input id="cn-jur" value={f.jurisdiction} onChange={set('jurisdiction')} />
            </Field>
            <Field
              label="Цель по выручке за год"
              htmlFor="cn-target"
              hint="В валюте страны; пусто — цели нет"
              error={errors.targetRevenue}
            >
              <Input
                id="cn-target"
                type="number"
                inputMode="decimal"
                min={0}
                value={f.targetRevenue}
                onChange={set('targetRevenue')}
              />
            </Field>
            <Field label="Заметки" htmlFor="cn-notes" className="sm:col-span-2" error={errors.notes}>
              <Textarea id="cn-notes" rows={2} value={f.notes} onChange={set('notes')} />
            </Field>
            <fieldset className="sm:col-span-2">
              <legend className="mb-2 text-[13px] font-medium text-ink">
                Готовность к продаже: {doneCount} из {COUNTRY_CHECKLIST.length}
              </legend>
              <ul className="flex flex-col gap-1">
                {COUNTRY_CHECKLIST.map((c) => (
                  <li key={c.code} className="flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-3">
                    <Checkbox
                      className="min-h-11 items-center sm:w-72"
                      label={c.title}
                      checked={Boolean(checklist[c.code]?.done)}
                      onChange={(e) =>
                        setChecklist((x) => ({
                          ...x,
                          [c.code]: {
                            ...x[c.code],
                            done: e.target.checked,
                            ...(e.target.checked ? { at: todayIso() } : {}),
                          },
                        }))
                      }
                    />
                    <Input
                      aria-label={`Примечание: ${c.title}`}
                      placeholder="Примечание"
                      maxLength={500}
                      value={checklist[c.code]?.note ?? ''}
                      onChange={(e) =>
                        setChecklist((x) => ({
                          ...x,
                          [c.code]: { done: Boolean(x[c.code]?.done), ...x[c.code], note: e.target.value },
                        }))
                      }
                    />
                  </li>
                ))}
              </ul>
            </fieldset>
          </>
        )}
        <ErrorBox className="sm:col-span-2" error={Object.keys(errors).length ? null : save.error} />
      </form>
    </Dialog>
  );
}

function CountriesPanel() {
  const q = useQuery(countriesQuery);
  const [editing, setEditing] = useState<Country | 'new' | null>(null);
  return (
    <Panel
      title="Страны"
      meta="МР-01, МР-02"
      actions={
        <Button
          variant="primary"
          size="sm"
          icon={<Plus className="size-4" />}
          onClick={() => setEditing('new')}
        >
          Добавить страну
        </Button>
      }
      bodyClassName="p-0"
    >
      <Loading q={q}>
        {(list) =>
          list.length ? (
            <ul className="divide-y divide-line">
              {list.map((c) => (
                <li key={c.code}>
                  <button
                    type="button"
                    onClick={() => setEditing(c)}
                    className="flex min-h-[44px] w-full flex-col gap-1 px-5 py-3 text-left hover:bg-surface-2 sm:flex-row sm:items-center sm:gap-4"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-medium text-ink">{c.name}</span>
                        <span className="text-[12px] text-ink-2">{c.code}</span>
                        <Badge tone={STATUS_TONE[c.status]}>{COUNTRY_STATUS_TITLES[c.status]}</Badge>
                        <Badge tone={SANCTION_TONE[c.sanctions]}>{SANCTION_TITLES[c.sanctions]}</Badge>
                      </div>
                      <div className="text-[13px] text-ink-2">
                        {c.currency} · языки: {c.languages.join(', ') || '—'} · данные:{' '}
                        {c.dataRegion ?? 'регион не задан'}
                      </div>
                    </div>
                    <div className="shrink-0 text-[13px] text-ink-2 sm:text-right">
                      Готовность: <span className="num font-medium text-ink">{c.checklistDone}</span> из{' '}
                      {c.checklistTotal}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <div className="p-5">
              <EmptyState title="Стран нет">Добавьте первую страну продаж.</EmptyState>
            </div>
          )
        }
      </Loading>
      {editing ? <CountryDialog country={editing} onClose={() => setEditing(null)} /> : null}
    </Panel>
  );
}

/* ───────────────────────── Налоги (МР-15) ───────────────────────── */

function CountrySelect({
  id,
  value,
  onChange,
}: {
  id: string;
  value: string;
  onChange: (v: string) => void;
}) {
  const q = useQuery(countriesQuery);
  return (
    <Select id={id} value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">выберите страну</option>
      {(q.data ?? []).map((c) => (
        <option key={c.code} value={c.code}>
          {c.name} ({c.code})
        </option>
      ))}
    </Select>
  );
}

function TaxDialog({ onClose }: { onClose: () => void }) {
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [f, setF] = useState({
    countryCode: '',
    code: 'VAT',
    name: 'НДС',
    rate: '',
    includedInPrice: false,
    validFrom: todayIso(),
    validTo: '',
  });
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) =>
    setF((x) => ({ ...x, [k]: e.target.value }));
  const save = useSave(
    () =>
      api('/platform/tax-rates', {
        body: {
          countryCode: f.countryCode,
          code: f.code,
          name: f.name,
          rate: Number(f.rate),
          includedInPrice: f.includedInPrice,
          validFrom: f.validFrom,
          validTo: f.validTo || null,
        },
      }),
    'Ставка добавлена',
    onClose,
    setErrors,
  );
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title="Новая ставка налога"
      description="Прежняя ставка с тем же кодом закрывается днём до начала новой."
      footer={dialogFooter('tax-form', onClose, save.isPending, 'Добавить')}
    >
      <form
        id="tax-form"
        onSubmit={(e) => {
          e.preventDefault();
          setErrors({});
          save.mutate(undefined);
        }}
        className="grid gap-4 sm:grid-cols-2"
        noValidate
      >
        <Field label="Страна" htmlFor="tx-country" error={errors.countryCode} className="sm:col-span-2">
          <CountrySelect
            id="tx-country"
            value={f.countryCode}
            onChange={(v) => setF((x) => ({ ...x, countryCode: v }))}
          />
        </Field>
        <Field label="Код" htmlFor="tx-code" error={errors.code}>
          <Input id="tx-code" value={f.code} onChange={set('code')} />
        </Field>
        <Field label="Название" htmlFor="tx-name" error={errors.name}>
          <Input id="tx-name" value={f.name} onChange={set('name')} />
        </Field>
        <Field label="Ставка, %" htmlFor="tx-rate" error={errors.rate}>
          <Input
            id="tx-rate"
            type="number"
            inputMode="decimal"
            min={0}
            max={100}
            step="0.01"
            value={f.rate}
            onChange={set('rate')}
          />
        </Field>
        <Checkbox
          className="min-h-11 items-center sm:mt-6"
          label="Входит в цену"
          description="Иначе налог добавляется сверху"
          checked={f.includedInPrice}
          onChange={(e) => setF((x) => ({ ...x, includedInPrice: e.target.checked }))}
        />
        <Field label="Действует с" htmlFor="tx-from" error={errors.validFrom}>
          <Input id="tx-from" type="date" value={f.validFrom} onChange={set('validFrom')} />
        </Field>
        <Field label="Действует по" htmlFor="tx-to" hint="Пусто — без окончания" error={errors.validTo}>
          <Input id="tx-to" type="date" value={f.validTo} onChange={set('validTo')} />
        </Field>
        <ErrorBox className="sm:col-span-2" error={Object.keys(errors).length ? null : save.error} />
      </form>
    </Dialog>
  );
}

function TaxPanel() {
  const q = useQuery(taxRatesQuery);
  const [adding, setAdding] = useState(false);
  return (
    <Panel
      title="Налоги"
      meta="МР-15"
      actions={
        <Button
          variant="secondary"
          size="sm"
          icon={<Plus className="size-4" />}
          onClick={() => setAdding(true)}
        >
          Новая ставка
        </Button>
      }
      bodyClassName="p-0"
    >
      <Loading q={q}>
        {(list: TaxRate[]) =>
          list.length ? (
            <ul className="divide-y divide-line">
              {list.map((t) => (
                <li
                  key={t.id}
                  className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 px-5 py-3"
                >
                  <div className="min-w-0">
                    <span className="font-medium text-ink">
                      {t.countryCode} · {t.name}
                    </span>{' '}
                    <span className="text-[12px] text-ink-2">{t.code}</span>
                    <div className="text-[13px] text-ink-2">
                      с {date(t.validFrom)}
                      {t.validTo ? ` по ${date(t.validTo)}` : ''} ·{' '}
                      {t.includedInPrice ? 'входит в цену' : 'сверху'}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="num text-[15px] font-semibold text-ink">{num(t.rate, 2)} %</span>
                    <Badge tone={t.isActive ? 'success' : 'neutral'}>
                      {t.isActive ? 'действует' : 'не действует'}
                    </Badge>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <div className="p-5">
              <EmptyState title="Ставок нет">Добавьте ставку налога для страны.</EmptyState>
            </div>
          )
        }
      </Loading>
      {adding ? <TaxDialog onClose={() => setAdding(false)} /> : null}
    </Panel>
  );
}

/* ───────────────────────── Курсы валют (МР-06) ───────────────────────── */

function RatesPanel() {
  const q = useQuery(currencyRatesQuery);
  const [f, setF] = useState({ currency: '', rateDate: todayIso(), rate: '' });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const add = useSave(
    () =>
      api('/platform/currency-rates', {
        body: { currency: f.currency, rateDate: f.rateDate, rate: Number(f.rate), source: 'manual' },
      }),
    'Курс сохранён',
    () => setF((x) => ({ ...x, rate: '' })),
    setErrors,
  );
  return (
    <Panel title="Курсы валют" meta="МР-06 · тенге за единицу валюты" bodyClassName="p-0">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setErrors({});
          add.mutate(undefined);
        }}
        className="grid gap-3 border-b border-line p-5 sm:grid-cols-[1fr_1fr_1fr_auto] sm:items-end"
        noValidate
      >
        <Field label="Валюта" htmlFor="cr-cur" error={errors.currency}>
          <Input
            id="cr-cur"
            maxLength={3}
            value={f.currency}
            onChange={(e) => setF((x) => ({ ...x, currency: e.target.value.toUpperCase() }))}
          />
        </Field>
        <Field label="На дату" htmlFor="cr-date" error={errors.rateDate}>
          <Input
            id="cr-date"
            type="date"
            value={f.rateDate}
            onChange={(e) => setF((x) => ({ ...x, rateDate: e.target.value }))}
          />
        </Field>
        <Field label="Курс" htmlFor="cr-rate" error={errors.rate}>
          <Input
            id="cr-rate"
            type="number"
            inputMode="decimal"
            min={0}
            step="any"
            value={f.rate}
            onChange={(e) => setF((x) => ({ ...x, rate: e.target.value }))}
          />
        </Field>
        <Button variant="primary" type="submit" loading={add.isPending} disabled={!f.currency || !f.rate}>
          Записать
        </Button>
        <ErrorBox className="sm:col-span-4" error={Object.keys(errors).length ? null : add.error} />
      </form>
      <Loading q={q}>
        {(list) =>
          list.length ? (
            <ul className="divide-y divide-line">
              {list.slice(0, 30).map((r) => (
                <li
                  key={r.id}
                  className="flex flex-wrap items-center justify-between gap-2 px-5 py-2.5 text-[14px]"
                >
                  <span className="text-ink">
                    <span className="font-medium">{r.currency}</span> · {date(r.rateDate)}
                  </span>
                  <span className="text-ink-2">
                    <span className="num font-medium text-ink">{num(r.rate, 4)}</span> · {r.source}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <div className="p-5">
              <EmptyState title="Курсов нет">
                Без курса выручка в других валютах не пересчитывается в тенге.
              </EmptyState>
            </div>
          )
        }
      </Loading>
    </Panel>
  );
}

/* ───────────────────────── Цены тарифов по странам (МР-07) ───────────────────────── */

function PricesPanel() {
  const plans = useQuery(plansQuery);
  const countries = useQuery(countriesQuery);
  const [planId, setPlanId] = useState('');
  const current = planId || plans.data?.[0]?.id || '';
  const prices = useQuery({ ...planPricesQuery(current), enabled: Boolean(current) });
  const [f, setF] = useState({
    countryCode: '',
    priceMonth: '',
    priceYear: '',
    currency: '',
    taxIncluded: false,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const setCountry = (code: string) => {
    const c = countries.data?.find((x) => x.code === code);
    setF((x) => ({ ...x, countryCode: code, currency: c?.currency ?? x.currency }));
  };
  const save = useSave(
    () =>
      api(`/platform/plans/${current}/prices`, {
        method: 'PUT',
        body: {
          countryCode: f.countryCode,
          priceMonth: f.priceMonth.trim() ? Number(f.priceMonth) : null,
          priceYear: f.priceYear.trim() ? Number(f.priceYear) : null,
          currency: f.currency,
          taxIncluded: f.taxIncluded,
        },
      }),
    'Цена сохранена',
    () => setF((x) => ({ ...x, priceMonth: '', priceYear: '' })),
    setErrors,
  );
  const del = useSave(
    (code: string) => api(`/platform/plans/${current}/prices/${code}`, { method: 'DELETE' }),
    'Цена удалена',
  );
  return (
    <Panel
      title="Цены тарифов по странам"
      meta="МР-07 · без цены страны действует цена тарифа"
      bodyClassName="p-0"
    >
      <div className="border-b border-line p-5">
        <Field label="Тариф" htmlFor="pp-plan">
          <Select id="pp-plan" value={current} onChange={(e) => setPlanId(e.target.value)}>
            {(plans.data ?? []).map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </Select>
        </Field>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setErrors({});
            save.mutate(undefined);
          }}
          className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-5 lg:items-end"
          noValidate
        >
          <Field label="Страна" htmlFor="pp-country" error={errors.countryCode}>
            <CountrySelect id="pp-country" value={f.countryCode} onChange={setCountry} />
          </Field>
          <Field label="В месяц" htmlFor="pp-m" error={errors.priceMonth}>
            <Input
              id="pp-m"
              type="number"
              inputMode="decimal"
              min={0}
              value={f.priceMonth}
              onChange={(e) => setF((x) => ({ ...x, priceMonth: e.target.value }))}
            />
          </Field>
          <Field label="В год" htmlFor="pp-y" error={errors.priceYear}>
            <Input
              id="pp-y"
              type="number"
              inputMode="decimal"
              min={0}
              value={f.priceYear}
              onChange={(e) => setF((x) => ({ ...x, priceYear: e.target.value }))}
            />
          </Field>
          <Field label="Валюта" htmlFor="pp-cur" error={errors.currency}>
            <Input
              id="pp-cur"
              maxLength={3}
              value={f.currency}
              onChange={(e) => setF((x) => ({ ...x, currency: e.target.value.toUpperCase() }))}
            />
          </Field>
          <Checkbox
            className="min-h-11 items-center"
            label="Налог в цене"
            checked={f.taxIncluded}
            onChange={(e) => setF((x) => ({ ...x, taxIncluded: e.target.checked }))}
          />
          <div className="sm:col-span-2 lg:col-span-5">
            <Button
              variant="primary"
              type="submit"
              loading={save.isPending}
              disabled={!current || !f.countryCode || !f.currency}
            >
              Сохранить цену
            </Button>
          </div>
          <ErrorBox
            className="sm:col-span-2 lg:col-span-5"
            error={Object.keys(errors).length ? null : save.error}
          />
        </form>
      </div>
      {current ? (
        <Loading q={prices}>
          {(list) =>
            list.length ? (
              <ul className="divide-y divide-line">
                {list.map((p) => (
                  <li
                    key={p.id}
                    className="flex flex-wrap items-center justify-between gap-2 px-5 py-2.5 text-[14px]"
                  >
                    <span className="font-medium text-ink">{p.countryCode}</span>
                    <span className="num text-ink-2">
                      {num(p.priceMonth)} / мес · {num(p.priceYear)} / год {p.currency}
                      {p.taxIncluded ? ' · налог в цене' : ''}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      icon={<Trash2 className="size-4" />}
                      aria-label={`Удалить цену для ${p.countryCode}`}
                      onClick={() => del.mutate(p.countryCode)}
                    />
                  </li>
                ))}
              </ul>
            ) : (
              <div className="p-5 text-[13px] text-ink-2">Для этого тарифа цен по странам нет.</div>
            )
          }
        </Loading>
      ) : null}
    </Panel>
  );
}

/* ───────────────────────── Правовые документы (МР-09) ───────────────────────── */

function LegalPanel() {
  const q = useQuery(legalQuery);
  const [adding, setAdding] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [f, setF] = useState({
    countryCode: '',
    kind: 'offer' as LegalKind,
    lang: 'ru',
    version: '',
    title: '',
    body: '',
  });
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) =>
    setF((x) => ({ ...x, [k]: e.target.value }));
  const close = () => {
    setAdding(false);
    setErrors({});
  };
  const save = useSave(
    () =>
      api('/platform/legal', {
        body: { ...f, countryCode: f.countryCode || null },
      }),
    'Документ опубликован',
    close,
    setErrors,
  );
  return (
    <Panel
      title="Правовые документы"
      meta="МР-09"
      actions={
        <Button
          variant="secondary"
          size="sm"
          icon={<Plus className="size-4" />}
          onClick={() => setAdding(true)}
        >
          Новая версия
        </Button>
      }
      bodyClassName="p-0"
    >
      <Loading q={q}>
        {(list) =>
          list.length ? (
            <ul className="divide-y divide-line">
              {list.map((d) => (
                <li
                  key={d.id}
                  className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 px-5 py-3"
                >
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium text-ink [overflow-wrap:anywhere]">{d.title}</span>
                      {d.isCurrent ? <Badge tone="success">действует</Badge> : <Badge>прежняя версия</Badge>}
                    </div>
                    <div className="text-[13px] text-ink-2">
                      {LEGAL_KIND_TITLES[d.kind]} · {d.countryCode ?? 'все страны'} · {d.lang} · версия{' '}
                      {d.version} · от {date(d.publishedAt)}
                    </div>
                  </div>
                  <span className="text-[13px] text-ink-2">принято: {d.acceptances}</span>
                </li>
              ))}
            </ul>
          ) : (
            <div className="p-5">
              <EmptyState title="Документов нет">
                Без оферты регистрация на главной странице не откроется.
              </EmptyState>
            </div>
          )
        }
      </Loading>
      {adding ? (
        <Dialog
          open
          onOpenChange={(v) => !v && close()}
          title="Новая версия документа"
          description="Прежняя версия того же вида, страны и языка перестанет действовать, принятые согласия сохранятся."
          width="lg"
          footer={dialogFooter('legal-form', close, save.isPending, 'Опубликовать')}
        >
          <form
            id="legal-form"
            onSubmit={(e) => {
              e.preventDefault();
              setErrors({});
              save.mutate(undefined);
            }}
            className="grid gap-4 sm:grid-cols-2"
            noValidate
          >
            <Field label="Вид" htmlFor="lg-kind" error={errors.kind}>
              <Select id="lg-kind" value={f.kind} onChange={set('kind')}>
                {LEGAL_KINDS.map((k) => (
                  <option key={k} value={k}>
                    {LEGAL_KIND_TITLES[k]}
                  </option>
                ))}
              </Select>
            </Field>
            <Field
              label="Страна"
              htmlFor="lg-country"
              hint="Пусто — для всех стран"
              error={errors.countryCode}
            >
              <CountrySelect
                id="lg-country"
                value={f.countryCode}
                onChange={(v) => setF((x) => ({ ...x, countryCode: v }))}
              />
            </Field>
            <Field label="Язык" htmlFor="lg-lang" error={errors.lang}>
              <Select id="lg-lang" value={f.lang} onChange={set('lang')}>
                {DOC_LANGS.map((l) => (
                  <option key={l} value={l}>
                    {l}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Версия" htmlFor="lg-ver" error={errors.version}>
              <Input id="lg-ver" value={f.version} onChange={set('version')} placeholder="2026-10" />
            </Field>
            <Field label="Название" htmlFor="lg-title" className="sm:col-span-2" error={errors.title}>
              <Input id="lg-title" value={f.title} onChange={set('title')} />
            </Field>
            <Field label="Текст" htmlFor="lg-body" className="sm:col-span-2" error={errors.body}>
              <Textarea id="lg-body" rows={10} value={f.body} onChange={set('body')} />
            </Field>
            <ErrorBox className="sm:col-span-2" error={Object.keys(errors).length ? null : save.error} />
          </form>
        </Dialog>
      ) : null}
    </Panel>
  );
}

/* ───────────────────────── Санкции (МР-23) ───────────────────────── */

function SanctionsPanel() {
  const qc = useQueryClient();
  const q = useQuery(sanctionsQuery);
  const [f, setF] = useState({ listCode: '', text: '', replace: true });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const imp = useMutation({
    mutationFn: () => api<{ imported: number }>('/platform/sanctions/import', { body: f }),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['platform', 'sanctions'] });
      toast(`Загружено записей: ${num(r.imported)}`);
      setF((x) => ({ ...x, text: '' }));
    },
    onError: (err) => err instanceof ApiError && setErrors(err.fieldErrors()),
  });
  const [names, setNames] = useState('');
  const screen = useMutation({
    mutationFn: () =>
      api<{
        allowed: boolean;
        reason: string | null;
        matches: Array<{ listCode: string; name: string; ref: string | null }>;
      }>('/platform/sanctions/screen', {
        body: {
          names: names
            .split('\n')
            .map((s) => s.trim())
            .filter((s) => s.length >= 2)
            .slice(0, 5),
        },
      }),
  });
  const onFile = async (file: File | undefined) => {
    if (!file) return;
    const text = await file.text();
    setF((x) => ({ ...x, text }));
  };
  return (
    <Panel title="Санкционные списки" meta="МР-23 · проверка при регистрации и заявках" bodyClassName="p-0">
      <Loading q={q}>
        {(list) =>
          list.length ? (
            <ul className="divide-y divide-line border-b border-line">
              {list.map((l) => (
                <li
                  key={l.listCode}
                  className="flex items-center justify-between gap-2 px-5 py-2.5 text-[14px]"
                >
                  <span className="font-medium text-ink">{l.listCode}</span>
                  <span className="num text-ink-2">{num(l.entries)} записей</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="border-b border-line p-5 text-[13px] text-ink-2">
              Списки не загружены — проверка ничего не находит.
            </p>
          )
        }
      </Loading>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setErrors({});
          imp.mutate();
        }}
        className="grid gap-4 border-b border-line p-5"
        noValidate
      >
        <Field label="Код списка" htmlFor="sn-code" hint="Например OFAC, EU, UN" error={errors.listCode}>
          <Input
            id="sn-code"
            value={f.listCode}
            onChange={(e) => setF((x) => ({ ...x, listCode: e.target.value.toUpperCase() }))}
          />
        </Field>
        <Field
          label="Записи"
          htmlFor="sn-text"
          hint="По одному названию в строке; после запятой — номер записи в списке"
          error={errors.text}
        >
          <Textarea
            id="sn-text"
            rows={5}
            value={f.text}
            onChange={(e) => setF((x) => ({ ...x, text: e.target.value }))}
          />
        </Field>
        <div className="flex flex-wrap items-center gap-3">
          <label className="inline-flex h-11 cursor-pointer items-center rounded-md border border-line-strong px-4 text-[14px] font-medium text-ink hover:underline">
            Загрузить из файла
            <input
              type="file"
              accept=".txt,.csv,text/plain,text/csv"
              className="sr-only"
              onChange={(e) => {
                void onFile(e.target.files?.[0]);
                e.target.value = '';
              }}
            />
          </label>
          <Checkbox
            className="min-h-11 items-center"
            label="Заменить список целиком"
            checked={f.replace}
            onChange={(e) => setF((x) => ({ ...x, replace: e.target.checked }))}
          />
          <Button
            variant="primary"
            type="submit"
            loading={imp.isPending}
            disabled={!f.listCode || !f.text.trim()}
          >
            Загрузить
          </Button>
        </div>
        <ErrorBox error={Object.keys(errors).length ? null : imp.error} />
      </form>
      <div className="grid gap-3 p-5">
        <Field label="Проверить названия" htmlFor="sn-screen" hint="До пяти названий, по одному в строке">
          <Textarea id="sn-screen" rows={2} value={names} onChange={(e) => setNames(e.target.value)} />
        </Field>
        <div>
          <Button
            variant="secondary"
            loading={screen.isPending}
            disabled={names.trim().length < 2}
            onClick={() => screen.mutate()}
          >
            Проверить
          </Button>
        </div>
        <ErrorBox error={screen.error} />
        {screen.data ? (
          <div role="status" className="text-[14px]">
            <Badge tone={screen.data.allowed ? 'success' : 'danger'}>
              {screen.data.allowed ? 'совпадений нет' : 'есть совпадения'}
            </Badge>
            {screen.data.reason ? <p className="mt-1 text-ink-2">{screen.data.reason}</p> : null}
            {screen.data.matches.length ? (
              <ul className="mt-2 text-[13px] text-ink-2">
                {screen.data.matches.map((m) => (
                  <li key={`${m.listCode}-${m.name}-${m.ref ?? ''}`} className="[overflow-wrap:anywhere]">
                    {m.listCode}: {m.name}
                    {m.ref ? ` (${m.ref})` : ''}
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        ) : null}
      </div>
    </Panel>
  );
}

/* ───────────────────────── Субпроцессоры (МР-10) ───────────────────────── */

function SubprocessorsEditor({ initial }: { initial: Subprocessor[] }) {
  const [rows, setRows] = useState<Subprocessor[]>(initial);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const save = useSave(
    () => api('/platform/subprocessors', { method: 'PUT', body: rows }),
    'Список сохранён',
    undefined,
    setErrors,
  );
  const upd = (i: number, k: keyof Subprocessor, v: string) =>
    setRows((r) => r.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        setErrors({});
        save.mutate(undefined);
      }}
      className="flex flex-col gap-4 p-5"
      noValidate
    >
      {rows.length ? (
        <ul className="flex flex-col gap-4">
          {rows.map((r, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: строки без собственного кода, порядок задаёт человек
            <li key={i} className="grid gap-3 sm:grid-cols-[1fr_1.5fr_0.8fr_auto] sm:items-end">
              <Field label="Название" htmlFor={`sp-n-${i}`} error={errors[`${i}.name`]}>
                <Input id={`sp-n-${i}`} value={r.name} onChange={(e) => upd(i, 'name', e.target.value)} />
              </Field>
              <Field label="Зачем" htmlFor={`sp-p-${i}`} error={errors[`${i}.purpose`]}>
                <Input
                  id={`sp-p-${i}`}
                  value={r.purpose}
                  onChange={(e) => upd(i, 'purpose', e.target.value)}
                />
              </Field>
              <Field label="Страна" htmlFor={`sp-c-${i}`} error={errors[`${i}.country`]}>
                <Input
                  id={`sp-c-${i}`}
                  value={r.country}
                  onChange={(e) => upd(i, 'country', e.target.value)}
                />
              </Field>
              <Button
                variant="ghost"
                aria-label={`Убрать ${r.name || 'строку'}`}
                icon={<Trash2 className="size-4" />}
                onClick={() => setRows((x) => x.filter((_, j) => j !== i))}
              />
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-[13px] text-ink-2">Список пуст.</p>
      )}
      <div className="flex flex-wrap gap-2">
        <Button
          variant="secondary"
          icon={<Plus className="size-4" />}
          onClick={() => setRows((r) => [...r, { name: '', purpose: '', country: '' }])}
        >
          Добавить строку
        </Button>
        <Button variant="primary" type="submit" loading={save.isPending}>
          Сохранить список
        </Button>
      </div>
      <ErrorBox error={Object.keys(errors).length ? null : save.error} />
    </form>
  );
}

function SubprocessorsPanel() {
  const q = useQuery(subprocessorsQuery);
  return (
    <Panel title="Субпроцессоры" meta="МР-10 · видят администраторы компаний" bodyClassName="p-0">
      <Loading q={q}>{(list) => <SubprocessorsEditor key={JSON.stringify(list)} initial={list} />}</Loading>
    </Panel>
  );
}

/** Страны и рынки (ТЗ 6.30): страны и чек-лист готовности, налоги, курсы, цены, документы, санкции, субпроцессоры */
export function MarketsPage() {
  return (
    <div className="flex flex-col gap-6">
      <CountriesPanel />
      <div className="grid gap-6 xl:grid-cols-2">
        <TaxPanel />
        <RatesPanel />
      </div>
      <PricesPanel />
      <LegalPanel />
      <div className="grid gap-6 xl:grid-cols-2">
        <SanctionsPanel />
        <SubprocessorsPanel />
      </div>
    </div>
  );
}
